// ── Header toolbar pieces ────────────────────────────────────────────────
// The controls a list page puts in AppLayout's header bar (`toolbar` on the
// left, `headerActions` on the right), so every page looks and behaves the
// same: search, filter buttons that name their choice, a date-range button,
// and a thin strip of figures under the header. Styles: `.tb-*` in
// index.css. Entity / Purchase / Sales use the same classes inline.

import type { ReactNode } from 'react';
import { Dropdown } from './ui';
import { type DateFilterKey, todayISO } from '../utils/ledgerReports';

function IconSearch() {
  return <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
    <circle cx="6" cy="6" r="4.2" stroke="currentColor" strokeWidth="1.4" />
    <path d="M9.2 9.2l3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>;
}
function IconChevronDownSm() {
  return <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
    <path d="M3 5.5l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
function IconCheckSm() {
  return <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
    <path d="M2.5 7.5l3 3 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
function IconCalendarSm() {
  return <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
    <rect x="2" y="3" width="12" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
    <path d="M2 6.5h12M5.5 1.5v3M10.5 1.5v3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>;
}
function IconFilterSm() {
  return <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
    <path d="M2 3.5h12l-4.5 5.2v4.3l-3-1.5V8.7L2 3.5z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
  </svg>;
}

/** Plus icon for a header "Add / New" button. */
export function IconPlusSm() {
  return <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
    <path d="M7 2.5v9M2.5 7h9" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
  </svg>;
}
/** Import (into the app) icon for a header tool group. */
export function IconImportSm() {
  return <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M7 9V1M4 3.5L7 .5l3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" transform="translate(0 1.5)" />
    <path d="M1.5 10.5v1.5a1 1 0 001 1h9a1 1 0 001-1v-1.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>;
}

/** Export (out of the app) icon for a header tool group. */
export function IconExportSm() {
  return <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M7 1v8M4 6.5L7 9.5l3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M1.5 10.5v1.5a1 1 0 001 1h9a1 1 0 001-1v-1.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>;
}

/** White search box with an icon and a × to clear. */
export function HeaderSearch({ value, onChange, placeholder, label, width }: {
  value: string; onChange: (v: string) => void; placeholder: string; label?: string; width?: number;
}) {
  return (
    <div className="tb-search" style={width ? { width } : undefined}>
      <span className="tb-search-icon"><IconSearch /></span>
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} aria-label={label ?? placeholder} />
      {value && <button type="button" className="tb-search-clear" onClick={() => onChange('')} title="Clear search" aria-label="Clear search"><svg width="8" height="8" viewBox="0 0 8 8" aria-hidden="true" style={{ display: 'block' }}><path d="M1.5 1.5l5 5M6.5 1.5l-5 5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" /></svg></button>}
    </div>
  );
}

export interface ChoiceOption<K extends string> { key: K; label: string; dot?: string; count?: number }

/** A filter as one button that names the current choice ("Status: Open");
 *  the options open in a menu. Highlighted while it isn't on `defaultKey`. */
export function ChoiceButton<K extends string>({ label, value, options, onChange, defaultKey }: {
  label: string; value: K; options: ChoiceOption<K>[]; onChange: (k: K) => void; defaultKey: K;
}) {
  const cur = options.find(o => o.key === value) ?? options[0];
  return (
    <Dropdown
      trigger={() => (
        <button type="button" className={`icon-btn${value !== defaultKey ? ' active' : ''}`} style={{ height: 34 }}>
          {cur.dot ? <span className="tb-dot" style={{ background: cur.dot }} /> : <IconFilterSm />}
          {`${label}: ${cur.label}`}
          <span style={{ color: 'var(--text-3)', display: 'flex' }}><IconChevronDownSm /></span>
        </button>
      )}
    >
      {options.map(o => (
        <button key={o.key} type="button" className="dropdown-item" onClick={() => onChange(o.key)}>
          <span style={{ width: 14, display: 'flex', color: 'var(--primary)', flexShrink: 0 }}>
            {value === o.key && <IconCheckSm />}
          </span>
          {o.dot && <span className="tb-dot" style={{ background: o.dot }} />}
          {o.label}
          {o.count != null && <span style={{ marginLeft: 'auto', opacity: 0.6, fontSize: 11, paddingLeft: 12 }}>{o.count}</span>}
        </button>
      ))}
    </Dropdown>
  );
}

const DATE_OPTIONS: { key: DateFilterKey; label: string }[] = [
  { key: 'this-month', label: 'This Month' },
  { key: '3-months',   label: '3 Months'   },
  { key: 'this-year',  label: 'This Year'  },
  { key: 'all',        label: 'All Time'   },
  { key: 'custom',     label: 'Custom'     },
];

/** Date range as one calendar button naming the range; Custom adds From / To boxes. */
export function DateRangeButton({ value, onChange, customFrom, customTo, onCustomFrom, onCustomTo, defaultKey = 'this-month' }: {
  value: DateFilterKey; onChange: (k: DateFilterKey) => void;
  customFrom: string; customTo: string;
  onCustomFrom: (v: string) => void; onCustomTo: (v: string) => void;
  defaultKey?: DateFilterKey;
}) {
  const cur = DATE_OPTIONS.find(o => o.key === value) ?? DATE_OPTIONS[0];
  return (
    <>
      <Dropdown
        trigger={() => (
          <button type="button" className={`icon-btn${value !== defaultKey ? ' active' : ''}`} style={{ height: 34 }}>
            <IconCalendarSm /> {cur.label}
            <span style={{ color: 'var(--text-3)', display: 'flex' }}><IconChevronDownSm /></span>
          </button>
        )}
      >
        {DATE_OPTIONS.map(o => (
          <button key={o.key} type="button" className="dropdown-item" onClick={() => onChange(o.key)}>
            <span style={{ width: 14, display: 'flex', color: 'var(--primary)', flexShrink: 0 }}>
              {value === o.key && <IconCheckSm />}
            </span>
            {o.label}
          </button>
        ))}
      </Dropdown>
      {value === 'custom' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="date" className="tb-date" value={customFrom} max={customTo || todayISO()}
            onChange={e => onCustomFrom(e.target.value)} aria-label="From date" />
          <span style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 600 }}>→</span>
          <input type="date" className="tb-date" value={customTo} min={customFrom || undefined} max={todayISO()}
            onChange={e => onCustomTo(e.target.value)} aria-label="To date" />
        </div>
      )}
    </>
  );
}

/** The same date ranges as DateRangeButton, as one row of tabs; Custom adds From / To boxes. */
export function DateRangeTabs({ value, onChange, customFrom, customTo, onCustomFrom, onCustomTo }: {
  value: DateFilterKey; onChange: (k: DateFilterKey) => void;
  customFrom: string; customTo: string;
  onCustomFrom: (v: string) => void; onCustomTo: (v: string) => void;
}) {
  return (
    <>
      <div className="tb-seg" role="tablist" aria-label="Period">
        {DATE_OPTIONS.map(o => (
          <button key={o.key} type="button" role="tab" aria-selected={value === o.key}
            className={value === o.key ? 'on' : undefined} onClick={() => onChange(o.key)}>
            {o.label}
          </button>
        ))}
      </div>
      {value === 'custom' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="date" className="tb-date" value={customFrom} max={customTo || todayISO()}
            onChange={e => onCustomFrom(e.target.value)} aria-label="From date" />
          <span style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 600 }}>→</span>
          <input type="date" className="tb-date" value={customTo} min={customFrom || undefined} max={todayISO()}
            onChange={e => onCustomTo(e.target.value)} aria-label="To date" />
        </div>
      )}
    </>
  );
}

export interface StatChip { label: string; value: ReactNode; tone?: 'danger' | 'primary' | 'success' | 'warning' }

/** Thin row of label | value figures under the header (replaces big stat cards). */
/** `end`: shown at the right end of the row (the column filter chips). */
export function StatChips({ items, end }: { items: StatChip[]; end?: ReactNode }) {
  return (
    <div className="tb-stats no-print">
      {items.map(it => (
        <div key={it.label} className="tb-stat">
          <span className="tb-stat-label">{it.label}</span>
          <span className="tb-stat-sep" />
          <span className="tb-stat-value" style={it.tone ? { color: `var(--${it.tone})` } : undefined}>{it.value}</span>
        </div>
      ))}
      {end}
    </div>
  );
}
